import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import useRedirect from "@/hooks/useRedirect";
import { useAuthStore } from "@/lib/auth/AuthStore";
import { mixpanelEvent } from "@/lib/mixpanels/mixpanel";
import useWindowDimensions from "@/lib/web-ui/useWindowDimensions";
import { CASH_INDEX_PAGE, EXTERNAL_WALLET_GUIDE, POINT_INDEX_PAGE, REGISTRATION_PAGE } from "@/settings/constant";
import { Button, Icon, TabContent, Tabs } from "@bookips/sds";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { useCallback, useRef } from "react";
import { useDialogStore } from "src/store/useDialogStore";
import { useAdBannerQuery } from "../Banner/AdBanner";
import Header from "./Header";
export const CASH_TAB_VALUE = "cash";
export const POINT_TAB_VALUE = "point";
const tabList = [
    {
        value: CASH_TAB_VALUE,
        label: "캐시",
        href: CASH_INDEX_PAGE
    },
    {
        value: POINT_TAB_VALUE,
        label: "포인트",
        href: POINT_INDEX_PAGE
    }
];
const WalletLayout = (param)=>{
    let { children , tabValue  } = param;
    const router = useRouter();
    const { isMobile , isTablet  } = useWindowDimensions();
    const { isLoggedIn , user  } = useAuthStore();
    const { open , close  } = useDialogStore();
    const { redirectTo , redirectToLogin  } = useRedirect();
    const isHandlingRef = useRef(false);
    const { adBannerResponse  } = useAdBannerQuery({
        position: "".concat(tabValue, "Home"),
        subject: user.mainSubject
    });
    const handleTabChange = useCallback((value)=>{
        var _tabList_find;
        // 중복 호출 방지
        if (isHandlingRef.current || value === tabValue) {
            return;
        }
        isHandlingRef.current = true;
        mixpanelEvent("Tab UI Clicked", {
            "Tab Type": value,
            "Tab Name": (_tabList_find = tabList.find((tab)=>tab.value === value)) === null || _tabList_find === void 0 ? void 0 : _tabList_find.label
        });
        if (!isLoggedIn && value === POINT_TAB_VALUE) {
            mixpanelEvent("Popup Viewed", {
                Title: "로그인이 필요해요.",
                position: "포인트 내역"
            });
            open({
                id: "login-required",
                type: "confirm",
                props: {
                    title: "로그인이 필요해요.",
                    description: "로그인 또는 회원가입을 진행해주세요.",
                    confirmText: "회원가입",
                    cancelText: "로그인",
                    onConfirm: ()=>{
                        mixpanelEvent("Popup Button Clicked", {
                            "Popup Title": "로그인이 필요해요.",
                            "Button Name": "회원가입",
                            position: "포인트 내역"
                        });
                        redirectTo(REGISTRATION_PAGE, POINT_INDEX_PAGE);
                    },
                    onCancel: ()=>{
                        mixpanelEvent("Popup Button Clicked", {
                            "Popup Title": "로그인이 필요해요.",
                            "Button Name": "로그인",
                            position: "포인트 내역"
                        });
                        redirectToLogin(POINT_INDEX_PAGE);
                    },
                    contentProps: {
                        onInteractOutside: ()=>{
                            close("login-required");
                        }
                    }
                }
            });
            isHandlingRef.current = false;
            return;
        }
        const selectedTab = tabList.find((tab)=>tab.value === value);
        router.push(selectedTab === null || selectedTab === void 0 ? void 0 : selectedTab.href);
        // router.push 후 플래그 리셋 (중복 호출 방지)
        setTimeout(()=>{
            isHandlingRef.current = false;
        }, 100);
    }, [
        tabValue,
        isLoggedIn,
        router,
        open,
        close,
        redirectTo,
        redirectToLogin
    ]);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Header, {
                title: "캐시/포인트",
                rightAction: /*#__PURE__*/ _jsx(Link, {
                    href: EXTERNAL_WALLET_GUIDE,
                    target: "_blank",
                    onClick: ()=>{
                        mixpanelEvent("Guide Button Clicked", {
                            "Guide Categories": "적립\xb7사용 방법",
                            "Guide URL": "https://solvookguide.oopy.io/cashpoint"
                        });
                    },
                    children: /*#__PURE__*/ _jsx(Button, {
                        variant: "stroke",
                        color: "secondary",
                        size: "sm",
                        leftElement: /*#__PURE__*/ _jsx(Icon.Info_2px, {
                            className: "size-4.5 text-primary"
                        }),
                        children: "적립∙사용 방법"
                    })
                })
            }),
            /*#__PURE__*/ _jsx(Tabs, {
                size: "lg",
                className: "mt-5",
                value: tabValue,
                classes: {
                    listContent: "max-w-[640px] px-5 m-auto"
                },
                onValueChange: handleTabChange,
                tabs: tabList,
                children: tabList.map((tab)=>{
                    var _adBannerResponse_items_;
                    /*#__PURE__*/ return _jsxs(TabContent, {
                        value: tab.value,
                        className: "bg-gray-300/20 h-full flex flex-col gap-6 pt-12",
                        children: [
                            (adBannerResponse === null || adBannerResponse === void 0 ? void 0 : adBannerResponse.items[0]) && /*#__PURE__*/ _jsx("div", {
                                className: "max-w-[640px] w-full mx-auto px-5",
                                children: /*#__PURE__*/ _jsx(Link, {
                                    href: ((_adBannerResponse_items_ = adBannerResponse === null || adBannerResponse === void 0 ? void 0 : adBannerResponse.items[0]) === null || _adBannerResponse_items_ === void 0 ? void 0 : _adBannerResponse_items_.link) || "",
                                    target: "_blank",
                                    onClick: ()=>{
                                        var _adBannerResponse_items_;
                                        mixpanelEvent("CashPoint Banner Clicked", {
                                            "Button Type": tabValue,
                                            "Button URL": ((_adBannerResponse_items_ = adBannerResponse === null || adBannerResponse === void 0 ? void 0 : adBannerResponse.items[0]) === null || _adBannerResponse_items_ === void 0 ? void 0 : _adBannerResponse_items_.link) || ""
                                        });
                                    },
                                    className: "flex justify-center w-full h-[80px] bg-gray-300/20 rounded-md overflow-hidden",
                                    children: /*#__PURE__*/ _jsx(Image, {
                                        src: isMobile ? (adBannerResponse === null || adBannerResponse === void 0 ? void 0 : adBannerResponse.items[0].imageUrlMobile) || (adBannerResponse === null || adBannerResponse === void 0 ? void 0 : adBannerResponse.items[0].imageUrl) : isTablet ? (adBannerResponse === null || adBannerResponse === void 0 ? void 0 : adBannerResponse.items[0].imageUrlTablet) || (adBannerResponse === null || adBannerResponse === void 0 ? void 0 : adBannerResponse.items[0].imageUrl) : (adBannerResponse === null || adBannerResponse === void 0 ? void 0 : adBannerResponse.items[0].imageUrlPc) || (adBannerResponse === null || adBannerResponse === void 0 ? void 0 : adBannerResponse.items[0].imageUrl),
                                        alt: "광고 이미지",
                                        width: 316,
                                        height: 80,
                                        sizes: "100%",
                                        className: "w-[316px] h-[80px]! object-cover"
                                    })
                                })
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "max-w-[640px] w-full mx-auto px-5 flex flex-col gap-6 pb-12",
                                children: children
                            })
                        ]
                    }, tab.value);
                })
            })
        ]
    });
};
export default WalletLayout;
